<template>
  <section class="mb-6">
    <h2 class="text-h6 mb-1">{{ title }}</h2>
    <p v-if="schedule" class="help-text mb-3">{{ schedule }}</p>
    <v-alert v-if="error" type="error" outlined dense>{{ error }}</v-alert>
    <v-alert v-if="message" type="success" outlined dense>{{ message }}</v-alert>
    <v-row>
      <v-col cols="12" :md="hasRdp ? 4 : 12">
        <v-card outlined class="fill-height">
          <v-card-title class="panel-title"><v-icon left>mdi-server</v-icon>EC2</v-card-title>
          <v-card-text>
            <div class="state-row">
              <v-chip :color="stateColor" dark>{{ status.state || "desconhecido" }}</v-chip>
              <span class="caption grey--text text--darken-1">{{ status.instanceType || "-" }}</span>
            </div>
            <div class="detail-list mt-4">
              <div><span>Instância</span><strong>{{ status.instanceId || "-" }}</strong></div>
              <div><span>Região</span><strong>{{ status.region || "-" }}</strong></div>
              <div><span>Status EC2</span><strong>{{ status.instanceStatus || "-" }}</strong></div>
              <div><span>Status sistema</span><strong>{{ status.systemStatus || "-" }}</strong></div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col v-if="hasRdp" cols="12" md="8">
        <v-card outlined class="fill-height">
          <v-card-title class="panel-title"><v-icon left>mdi-remote-desktop</v-icon>Conexão RDP</v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="6"><div class="detail-list">
                <div><span>Host atual</span><strong>{{ status.rdpHost || "-" }}</strong></div>
                <div><span>IP público</span><strong>{{ status.publicIpAddress || "-" }}</strong></div>
                <div><span>DNS público</span><strong>{{ status.publicDnsName || "-" }}</strong></div>
              </div></v-col>
              <v-col cols="12" md="6"><div class="detail-list">
                <div><span>Porta</span><strong>{{ status.rdpPort || 3389 }}</strong></div>
                <div><span>Usuário</span><strong>{{ status.rdpUsername || "Administrator" }}</strong></div>
                <div v-if="agentLabel"><span>Agente EC2</span><strong>{{ agentLabel }}</strong></div>
              </div></v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-card outlined class="mt-4">
      <v-card-title class="panel-title"><v-icon left>mdi-console</v-icon>Máquina</v-card-title>
      <v-card-text>
        <div class="action-grid">
          <v-btn color="success" :loading="busy.start" :disabled="runningLike || !status.instanceId" @click="changePower('start')"><v-icon left>mdi-play</v-icon>Ligar EC2</v-btn>
          <v-btn color="error" outlined :loading="busy.stop" :disabled="stoppedLike || !status.instanceId" @click="changePower('stop')"><v-icon left>mdi-stop</v-icon>Desligar EC2</v-btn>
          <v-btn v-if="machineKey === 'codex'" color="primary" outlined :loading="busy.allow" :disabled="!status.manualRdpEnabled || !status.rdpAvailable || busy.allow" @click="prepareRdpAccess"><v-icon left>mdi-shield-key</v-icon>Autorizar meu IP</v-btn>
          <v-btn v-if="hasRdp" color="primary" :loading="busy.rdp" :disabled="!status.rdpAvailable" @click="downloadRdp"><v-icon left>mdi-download</v-icon>Baixar RDP</v-btn>
          <v-btn outlined :disabled="!status.rdpHost" @click="copyHost"><v-icon left>mdi-content-copy</v-icon>Copiar host</v-btn>
        </div>
        <v-progress-linear v-if="transitioning" indeterminate color="primary" class="mt-4" />
        <p v-if="machineKey === 'codex'" class="help-text mt-3 mb-0">Quando seu IP mudar, clique em Autorizar meu IP, confira o destino e confirme. Depois baixe o RDP atualizado. A regra /32 permanece até remoção manual; não há renovação automática.</p>
        <p v-if="machineKey === 'codex' && !status.manualRdpEnabled" class="help-text">Autorização manual aguardando ativação aprovada.</p>
        <p v-if="hasRdp" class="help-text mt-3 mb-0">O RDP traz o host e o usuário. Neste dispositivo, a senha já salva no cliente de Área de Trabalho Remota pode ser reutilizada.</p>
      </v-card-text>
    </v-card>
    <v-dialog v-model="showRdpConfirmation" max-width="640" persistent>
      <v-card v-if="rdpPreview">
        <v-card-title>Confirmar autorização RDP</v-card-title>
        <v-card-text>
          <p>Autorizar somente o IPv4 público deste navegador: <strong>{{ rdpPreview.cidr }}</strong>.</p>
          <div class="detail-list">
            <div><span>Instância / região</span><strong>{{ rdpPreview.instanceId }} / {{ rdpPreview.region }}</strong></div>
            <div><span>Security Group</span><strong>{{ rdpPreview.securityGroupId }}</strong></div>
            <div><span>Host atual</span><strong>{{ rdpPreview.host }}</strong></div>
            <div><span>Protocolo / porta</span><strong>TCP / 3389</strong></div>
            <div><span>Confirmação válida até</span><strong>{{ new Date(rdpPreview.expiresUtc).toLocaleTimeString("pt-BR") }}</strong></div>
          </div>
          <p class="mt-3">Esta confirmação dura dois minutos. A regra de acesso permanece até remoção manual. Regras existentes serão preservadas.</p>
        </v-card-text>
        <v-card-actions>
          <v-btn text :disabled="busy.allow" @click="cancelRdpAccess">Cancelar</v-btn>
          <v-spacer />
          <v-btn color="primary" :loading="busy.allow" :disabled="busy.allow" @click="confirmRdpAccess">Confirmar este IP /32</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </section>
</template>

<script>
import { ec2CodexAPI } from "../API/Ec2CodexAPI";

export default {
  name: "Ec2MachineCard",
  props: { machineKey: { type: String, required: true }, title: { type: String, required: true }, schedule: { type: String, default: "" }, agentLabel: { type: String, default: "" }, hasRdp: { type: Boolean, default: true } },
  data: () => ({ status: {}, error: "", message: "", rdpPreview: null, showRdpConfirmation: false, busy: { start: false, stop: false, rdp: false, allow: false } }),
  computed: {
    state() { return String(this.status.state || "").toLowerCase(); },
    runningLike() { return ["pending", "running"].includes(this.state); },
    stoppedLike() { return ["stopping", "stopped", "shutting-down", "terminated"].includes(this.state); },
    transitioning() { return ["pending", "stopping", "shutting-down"].includes(this.state); },
    stateColor() { return ({ running: "success", pending: "warning", stopping: "warning", stopped: "grey", terminated: "error" })[this.state] || "primary"; },
  },
  mounted() { this.refresh(); },
  methods: {
    async browserPublicIpv4() {
      // Executed by the user's browser, never by the API server or assistant.
      const controller = new AbortController();
      const timer = window.setTimeout(() => controller.abort(), 10000);
      try {
        const response = await fetch("https://api.ipify.org?format=json", { cache: "no-store", credentials: "omit", referrerPolicy: "no-referrer", signal: controller.signal });
        if (!response.ok) throw new Error("Não foi possível consultar o IPv4 público deste navegador.");
        const result = await response.json();
        if (!/^(\d{1,3}\.){3}\d{1,3}$/.test(result.ip || "")) throw new Error("Esta operação requer IPv4 público.");
        return result.ip;
      } finally { window.clearTimeout(timer); }
    },
    async prepareRdpAccess() {
      if (this.busy.allow) return;
      this.busy.allow = true; this.error = ""; this.message = ""; this.rdpPreview = null;
      try {
        const browserIpv4 = await this.browserPublicIpv4();
        this.rdpPreview = (await ec2CodexAPI.previewRdpAccess(this.machineKey, { browserIpv4 })).data;
        this.showRdpConfirmation = true;
      } catch (error) { this.error = this.extractError(error, "Não foi possível preparar a autorização. Verifique sua rede e tente novamente."); }
      finally { this.busy.allow = false; }
    },
    cancelRdpAccess() { this.showRdpConfirmation = false; this.rdpPreview = null; },
    async confirmRdpAccess() {
      if (this.busy.allow || !this.rdpPreview) return;
      this.busy.allow = true; this.error = "";
      try {
        if (Date.now() >= Date.parse(this.rdpPreview.expiresUtc)) throw new Error("Confirmação expirada. Clique novamente em Autorizar meu IP.");
        const browserIpv4 = await this.browserPublicIpv4();
        if (browserIpv4 + "/32" !== this.rdpPreview.cidr) throw new Error("Seu IP mudou. Clique novamente em Autorizar meu IP.");
        const response = await ec2CodexAPI.confirmRdpAccess(this.machineKey, { browserIpv4, approval: this.rdpPreview.approval, confirmed: true });
        this.message = `IP ${response.data.cidr} autorizado para TCP 3389 na instância ${response.data.instanceId}. Agora clique em Baixar RDP.`;
      } catch (error) { this.error = this.extractError(error, "Resultado da autorização não confirmado. Consulte as regras antes de tentar novamente."); }
      finally { this.cancelRdpAccess(); this.busy.allow = false; }
    },
    async refresh() {
      try { this.status = (await ec2CodexAPI.machineStatus(this.machineKey)).data || {}; this.error = ""; }
      catch (error) { this.error = this.extractError(error, "Não foi possível consultar a EC2."); }
    },
    async changePower(action) {
      this.busy[action] = true; this.error = "";
      try { this.status = (await ec2CodexAPI.machinePower(this.machineKey, action)).data || {}; }
      catch (error) { this.error = this.extractError(error, "Não foi possível alterar o estado da EC2."); }
      finally { this.busy[action] = false; }
    },
    async downloadRdp() {
      this.busy.rdp = true; this.error = "";
      try {
        const response = await ec2CodexAPI.machineRdp(this.machineKey);
        const url = URL.createObjectURL(new Blob([response.data], { type: "application/x-rdp" }));
        const link = document.createElement("a"); link.href = url; link.download = this.machineKey === "backup" ? "ec2-backups.rdp" : "codex-ec2-ohio.rdp";
        document.body.appendChild(link); link.click(); link.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 30000);
      } catch (error) { this.error = this.extractError(error, "Não foi possível baixar o RDP."); }
      finally { this.busy.rdp = false; }
    },
    async copyHost() {
      try { await navigator.clipboard.writeText(this.status.rdpHost); }
      catch (error) { this.error = "Não foi possível copiar o host."; }
    },
    extractError(error, fallback) {
      if (error && error.response && error.response.status === 401) return "Sua sessão do painel expirou. Entre novamente e confirme o IP.";
      if (error && error.response && error.response.status === 403) return "Somente o fulladmin pode autorizar o IP.";
      const data = error && error.response && error.response.data;
      if (data && data.mensagem) return data.mensagem;
      if (error && error.response) return `${fallback} (HTTP ${error.response.status}).`;
      return (error && error.message) || fallback;
    },
  },
};
</script>

<style scoped>
.panel-title { font-size: 16px; font-weight: 600; }
.state-row { align-items: center; display: flex; gap: 12px; }
.detail-list { display: grid; gap: 12px; }
.detail-list div { display: grid; gap: 2px; min-width: 0; }
.detail-list span { color: #6b7280; font-size: 12px; text-transform: uppercase; }
.detail-list strong { color: #1f2937; font-size: 14px; font-weight: 600; overflow-wrap: anywhere; }
.help-text { color: #4b5563; font-size: 13px; }
.action-grid { display: flex; flex-wrap: wrap; gap: 10px; }
@media (max-width: 600px) { .action-grid .v-btn { width: 100%; } }
</style>
